/**
 * Chatbot WON amélioré avec IA
 * Version intelligente qui utilise OpenAI pour des réponses naturelles
 * Conserve l'interface existante mais ajoute l'intelligence artificielle
 */

import React, { useState, useEffect, useRef } from 'react';
import ChatbotAIService, { type ChatResponse, type QuickAction } from '../../services/chatbot-ai.service';

/**
 * Convertit un markdown très simple en HTML (minimal).
 */
const formatMessage = (content: string): string => {
  return content
    // Gras **texte**
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italique *texte* (éviter conflit avec **)
    .replace(/(^|[^*])\*(?!\*)([^*]+)\*(?!\*)/g, '$1<em>$2</em>')
    // Saut de ligne
    .replace(/\n/g, '<br/>')
    // Listes • ou - (pas de style inline pour sécurité)
    .replace(/^[•\-]\s(.+)$/gm, '<div>• $1</div>');
};

/**
 * Sanitize HTML avec whitelist très limitée (strong, em, br, div, p).
 * Objectif: éviter XSS tant que DOMPurify n'est pas installé.
 */
const sanitizeHTML = (dirtyHtml: string): string => {
  try {
    // Si DOMPurify est disponible (via CDN ou intégration), l'utiliser
    const dp: any = (window as any)?.DOMPurify;
    if (dp && typeof dp.sanitize === 'function') {
      return dp.sanitize(dirtyHtml, {
        ALLOWED_TAGS: ['strong', 'em', 'br', 'div', 'p'],
        ALLOWED_ATTR: [],
      });
    }
  } catch {}
  try {
    const allowed = new Set(['strong', 'em', 'br', 'div', 'p']);
    const wrap = document.createElement('div');
    wrap.innerHTML = dirtyHtml;

    const cleanNode = (node: Node): Node | null => {
      if (node.nodeType === Node.TEXT_NODE) return document.createTextNode(node.textContent || '');
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const tag = el.tagName.toLowerCase();
        if (!allowed.has(tag)) return document.createTextNode(el.textContent || '');
        const safe = document.createElement(tag);
        for (const child of Array.from(el.childNodes)) {
          const cleaned = cleanNode(child);
          if (cleaned) safe.appendChild(cleaned);
        }
        return safe;
      }
      return null;
    };

    const out = document.createElement('div');
    for (const child of Array.from(wrap.childNodes)) {
      const cleaned = cleanNode(child);
      if (cleaned) out.appendChild(cleaned);
    }
    return out.innerHTML;
  } catch (e) {
    // Fallback: échapper tout
    const escape = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return escape(String(dirtyHtml || ''));
  }
};

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  quickActions?: QuickAction[];
  timestamp: Date;
  isLoading?: boolean;
}

interface SmartChatbotProps {
  openaiApiKey?: string;
  position?: 'bottom-right' | 'bottom-left';
  theme?: 'light' | 'dark';
  autoInit?: boolean;
}

const SmartChatbot: React.FC<SmartChatbotProps> = ({ 
  openaiApiKey,
  position = 'bottom-right',
  theme = 'light',
  autoInit = true 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [aiService, setAiService] = useState<ChatbotAIService | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);
  const composerSuggestions = [
    'Demander un devis',
    'Parler à un expert',
    'Faire un audit gratuit'
  ];

  const formatTimestamp = (d: Date) => {
    try {
      const dt = new Date(d);
      return dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  // Initialisation du service IA
  useEffect(() => {
    console.log('🤖 Initialisation SmartChatbot avec clé API:', openaiApiKey ? 'PRÉSENTE' : 'ABSENTE');
    
    if (openaiApiKey && openaiApiKey !== 'configured') {
      try {
        console.log('✅ Création du service IA...');
        const service = new ChatbotAIService(openaiApiKey);
        setAiService(service);
        
        // Message d'accueil personnalisé
        const welcomeMessage: Message = {
          id: '1',
          content: "Bonjour ! Je suis WON, votre assistant IA de World Of Numeric. 🚀\n\nJe vous accompagne dans votre transformation digitale avec des conseils personnalisés et des solutions concrètes. Parlez-moi de vos défis numériques !",
          role: 'assistant',
          timestamp: new Date(),
          quickActions: [
            { id: 'rgpd', label: 'Conformité RGPD', emoji: '🔒' },
            { id: 'cybersec', label: 'Cybersécurité', emoji: '🛡️' },
            { id: 'facturation', label: 'Facturation électronique', emoji: '📊' },
            { id: 'presence', label: 'Présence en ligne', emoji: '🌐' },
            { id: 'formation_ia', label: 'Formation IA', emoji: '🤖' }
          ]
        };
        
        setMessages([welcomeMessage]);
        console.log('✅ Service IA initialisé avec succès');
      } catch (error) {
        console.error('❌ Erreur initialisation chatbot IA:', error);
        setMessages([getFallbackWelcomeMessage()]);
      }
    } else {
      // Mode fallback sans IA
      console.log('⚠️ Mode fallback activé - pas de clé API');
      setMessages([getFallbackWelcomeMessage()]);
    }
  }, [openaiApiKey]);

  // Auto-scroll vers le bas
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Détecter petite taille d'écran (drawer mobile)
  useEffect(() => {
    const onResize = () => setIsSmallScreen(window.innerWidth < 640);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Fermer sur ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getFallbackWelcomeMessage = (): Message => ({
    id: '1',
    content: "Bonjour ! Je suis WON, votre assistant de World Of Numeric. 🚀\n\nJe vous accompagne dans votre transformation digitale. Comment puis-je vous aider aujourd'hui ?",
    role: 'assistant',
    timestamp: new Date(),
    quickActions: [
      { id: 'rgpd', label: 'Conformité RGPD', emoji: '🔒' },
      { id: 'cybersec', label: 'Cybersécurité', emoji: '🛡️' },
      { id: 'facturation', label: 'Facturation électronique', emoji: '📊' },
      { id: 'presence', label: 'Présence en ligne', emoji: '🌐' },
      { id: 'contact', label: 'Prendre contact', emoji: '📞' }
    ]
  });

  /**
   * Gestion de l'envoi de message avec IA
   */
  const handleSendMessage = async () => {
    if (!message.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: message,
      role: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setMessage('');
    setIsLoading(true);
    setIsTyping(true);

    console.log('💬 Traitement message:', message);
    console.log('🤖 Service IA disponible:', !!aiService);

    let assistantId: string | null = null;
    try {
      let response: ChatResponse;
      
      if (aiService) {
        // Utiliser l'IA avec streaming backend si possible
        console.log('🧠 Appel de l\'IA (streaming si possible)...');
        // Créer un message assistant vide pour afficher le flux
        assistantId = (Date.now() + 1).toString();
        const placeholder: Message = {
          id: assistantId,
          content: '',
          role: 'assistant',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, placeholder]);

        const streamed = await aiService.generateResponseStreaming(userMessage.content, (delta) => {
          setIsTyping(true);
          setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: (m.content || '') + delta } : m));
        });

        if (streamed) {
          response = streamed;
        } else {
          // Fallback non-streaming
          response = await aiService.generateResponse(userMessage.content);
        }

        if (response.isError && response.errorMessage?.includes('429')) {
          console.log('⚠️ Limite OpenAI - Mode expert activé automatiquement');
          // En cas d'erreur 429, l'utilisateur aura quand même une excellente réponse
        } else if (!response.isError) {
          console.log('✅ Réponse IA reçue:', response.content.substring(0, 100) + '...');
        }
      } else {
        // Fallback sans IA
        console.log('⚠️ Utilisation du fallback - pas d\'IA');
        response = await getFallbackResponse(message);
      }

      // Finaliser: si un message assistant placeholder existe, le remplacer
      setIsTyping(false);
      setMessages(prev => {
        if (assistantId) {
          const hasPlaceholder = prev.find(m => m.id === assistantId);
          if (hasPlaceholder) {
            return prev.map(m => m.id === assistantId
              ? { ...m, content: response.content, quickActions: response.quickActions }
              : m
            );
          }
        }
        const botMessage: Message = {
          id: (Date.now() + 2).toString(),
          content: response.content,
          role: 'assistant',
          timestamp: new Date(),
          quickActions: response.quickActions
        };
        return [...prev, botMessage];
      });
      setIsLoading(false);

    } catch (error) {
      console.error('❌ Erreur envoi message:', error);
      setIsTyping(false);
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: "Je vais vous donner une réponse experte immédiatement ! 🚀\n\nNos systèmes sont en cours d'optimisation mais je peux quand même vous aider efficacement.",
        role: 'assistant',
        timestamp: new Date(),
        quickActions: [
          { id: 'contact_expert', label: 'Parler à un expert', emoji: '👨‍💼' },
          { id: 'audit_gratuit', label: 'Audit gratuit', emoji: '📋' }
        ]
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
    }
  };

  /**
   * Gestion des actions rapides avec vraies redirections
   */
  const handleQuickAction = async (actionId: string, label: string) => {
    if (isLoading) return;

    console.log('⚡ Action rapide:', actionId, label);

    // Actions de redirection directe SANS passer par le chat
    const directRedirections: Record<string, () => void> = {
      'ouvrir_formulaire_contact': () => {
        console.log('🌐 Redirection vers formulaire de contact');
        window.open('contact.html', '_blank');
      },
      'ouvrir_formulaire': () => {
        console.log('🌐 Ouverture formulaire');
        window.open('contact.html', '_blank');
      },
      'formulaire_contact': () => {
        console.log('🌐 Redirection vers page contact');
        window.open('contact.html', '_blank');
      },
      'formulaire_devis': () => {
        console.log('📋 Redirection vers formulaire de devis');
        window.open('contact.html?sujet=devis', '_blank');
      },
      'formulaire_rgpd': () => {
        console.log('🔒 Redirection vers formulaire RGPD');
        window.open('contact.html?sujet=rgpd', '_blank');
      },
      'formulaire_cyber': () => {
        console.log('🛡️ Redirection vers formulaire cybersécurité');
        window.open('contact.html?sujet=cybersecurite', '_blank');
      },
      'voir_realisations': () => {
        console.log('👁️ Redirection vers réalisations');
        window.open('realisations.html', '_blank');
      },
      'voir_portfolio': () => {
        console.log('👁️ Redirection vers portfolio');
        window.open('realisations.html', '_blank');
      },
      'voir_references': () => {
        console.log('⭐ Redirection vers références/portfolio');
        window.open('realisations.html', '_blank');
      },
      'contact_expert': () => {
        console.log('👨‍💼 Contact expert');
        window.open('contact.html?sujet=expert', '_blank');
      },
      'rdv_rappel': () => {
        console.log('🔄 Demander un rappel');
        window.open('contact.html?sujet=rdv', '_blank');
      },
      'voir_horaires': () => {
        console.log('🕐 Voir horaires');
        window.open('contact.html#horaires', '_blank');
      },
    };

    // Si c'est une redirection directe, l'exécuter et ajouter message de confirmation
    if (directRedirections[actionId]) {
      console.log('⚡ Exécution redirection directe pour:', actionId);
      directRedirections[actionId]();
      
      // Message de confirmation dans le chat
      const confirmationMessage: Message = {
        id: Date.now().toString(),
        content: `✅ **Redirection en cours...**\n\n*Page en cours d'ouverture dans un nouvel onglet.*\n\nSi la page ne s'ouvre pas automatiquement :\n📧 **Email :** contact@worldofnumeric.fr`,
        role: 'assistant',
        timestamp: new Date(),
        quickActions: [
          { id: 'aide_contact', label: 'Besoin d\'aide ?', emoji: '❓' }
        ]
      };
      
      setMessages(prev => [...prev, confirmationMessage]);
      return;
    }

    // Pour les autres actions, traitement normal avec conversation (stream si possible)
    const userMessage: Message = {
      id: Date.now().toString(),
      content: label,
      role: 'user',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setIsTyping(true);

    let assistantId: string | null = null;
    try {
      let response: ChatResponse;
      
      if (aiService) {
        console.log('🧠 Appel IA pour action rapide (stream si possible)...');
        // Placeholder assistant
        assistantId = (Date.now() + 1).toString();
        const placeholder: Message = { id: assistantId, content: '', role: 'assistant', timestamp: new Date() };
        setMessages(prev => [...prev, placeholder]);

        // On essaie de streamer via prompt de contexte dérivé de l'action
        let streamed: ChatResponse | null = null;
        try {
          streamed = await aiService.generateResponseStreaming(label, (delta) => {
            setIsTyping(true);
            setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: (m.content || '') + delta } : m));
          });
        } catch {}

        if (streamed) {
          response = streamed;
        } else {
          response = await aiService.handleQuickAction(actionId, label);
        }
        console.log('✅ Réponse IA action reçue:', response.content.substring(0, 100) + '...');
      } else {
        console.log('⚠️ Utilisation fallback pour action rapide');
        response = await getFallbackActionResponse(actionId, label);
      }

      setIsTyping(false);
      setMessages(prev => {
        if (assistantId) {
          const hasPlaceholder = prev.find(m => m.id === assistantId);
          if (hasPlaceholder) {
            return prev.map(m => m.id === assistantId
              ? { ...m, content: response.content, quickActions: response.quickActions }
              : m
            );
          }
        }
        const botMessage: Message = {
          id: (Date.now() + 2).toString(),
          content: response.content,
          role: 'assistant',
          timestamp: new Date(),
          quickActions: response.quickActions
        };
        return [...prev, botMessage];
      });
      setIsLoading(false);

    } catch (error) {
      console.error('❌ Erreur action rapide:', error);
      setIsTyping(false);
      setIsLoading(false);
    }
  };

  /**
   * Réponse fallback avec détection d'intention de contact
   */
  const getFallbackResponse = async (userMessage: string): Promise<ChatResponse> => {
    const lowerMessage = userMessage.toLowerCase().trim();
    
    console.log('🔍 Détection intention pour:', lowerMessage);
    
    // Détection intention CONTACT - Priorité absolue
    if (
      lowerMessage.includes('contacter won') ||
      lowerMessage.includes('contact won') ||
      lowerMessage.includes('joindre won') ||
      lowerMessage.includes('contacter world') ||
      lowerMessage.includes('contact world') ||
      (lowerMessage.includes('contact') && (lowerMessage.includes('won') || lowerMessage.includes('world'))) ||
      lowerMessage === 'contact' ||
      lowerMessage === 'contacter' ||
      lowerMessage.includes('prendre contact') ||
      lowerMessage.includes('nous contacter') ||
      lowerMessage.includes('vous contacter') ||
      lowerMessage.includes('formulaire de contact') ||
      lowerMessage.includes('formulaire') ||
      lowerMessage.includes('joindre') ||
      lowerMessage.includes('rdv') ||
      lowerMessage.includes('rendez-vous')
    ) {
      console.log('✅ Intention CONTACT détectée');
      return {
        content: "📞 **Contactez World Of Numeric :**\n\n🌐 **Formulaire en ligne :** Cliquez sur le bouton ci-dessous pour accéder directement à notre formulaire de contact\n📧 **Email :** contact@worldofnumeric.fr\n\n*Le formulaire s'ouvrira dans un nouvel onglet.*",
        quickActions: [
          { id: 'ouvrir_formulaire_contact', label: 'Ouvrir le formulaire', emoji: '🌐' }
        ]
      };
    }

    // Détection intention DEVIS
    if (lowerMessage.includes('devis') || lowerMessage.includes('prix') || lowerMessage.includes('tarif')) {
      return {
        content: "💰 **Demande de devis personnalisé :**\n\nPour obtenir un devis adapté à vos besoins, remplissez notre formulaire dédié.\n\n📋 **Devis gratuit**\n🎯 **Étude personnalisée de votre projet**",
        quickActions: [
          { id: 'formulaire_devis', label: 'Demander un devis', emoji: '📋' },
          { id: 'ouvrir_formulaire_contact', label: 'Contact général', emoji: '📞' }
        ]
      };
    }
    
    // Réponses spécialisées selon le sujet SANS supposer de contexte
    if (lowerMessage.includes('rgpd') || lowerMessage.includes('données') || lowerMessage.includes('conform')) {
      return {
        content: "Le RGPD est effectivement crucial ! Pour vous donner des conseils adaptés, pouvez-vous me dire :\n\n• Dans quel secteur travaillez-vous ?\n• Combien d'employés dans votre entreprise ?\n• Traitez-vous des données sensibles ?\n\nOu contactez directement notre expert RGPD :",
        quickActions: [
          { id: 'formulaire_rgpd', label: 'Expert RGPD', emoji: '🔒' },
          { id: 'secteur_medical', label: 'Secteur médical', emoji: '🏥' },
          { id: 'secteur_commerce', label: 'Commerce/Services', emoji: '🛒' }
        ]
      };
    }
    
    if (lowerMessage.includes('cyber') || lowerMessage.includes('sécurité') || lowerMessage.includes('hack')) {
      return {
        content: "La cybersécurité est essentielle ! Pour évaluer vos risques précisément :\n\n• Quelle est la taille de votre entreprise ?\n• Dans quel secteur évoluez-vous ?\n• Avez-vous déjà eu des incidents ?\n\nOu contactez notre expert cybersécurité :",
        quickActions: [
          { id: 'formulaire_cyber', label: 'Expert cybersécurité', emoji: '🛡️' },
          { id: 'evaluer_risques', label: 'Évaluer mes risques', emoji: '🔍' }
        ]
      };
    }
    
    if (lowerMessage.includes('site') || lowerMessage.includes('web') || lowerMessage.includes('référencement')) {
      return {
        content: "Créer ou améliorer votre site web, excellente idée ! Pour vous proposer la meilleure solution :\n\n• Avez-vous déjà un site web ?\n• Quel est votre secteur d'activité ?\n• Quels sont vos objectifs ?\n\nOu demandez directement un devis :",
        quickActions: [
          { id: 'formulaire_devis', label: 'Devis site web', emoji: '📋' },
          { id: 'voir_realisations', label: 'Voir nos réalisations', emoji: '👁️' }
        ]
      };
    }
    
    // Réponse générale qui demande le contexte
    return {
      content: "Je suis là pour vous accompagner dans votre transformation digitale ! Pour vous conseiller au mieux :\n\n• Dans quel secteur travaillez-vous ?\n• Quelle est votre principale préoccupation ?\n• Préférez-vous qu'on vous rappelle directement ?",
      quickActions: [
        { id: 'ouvrir_formulaire_contact', label: 'Nous contacter', emoji: '📞' },
        { id: 'comprendre_besoins', label: 'Analyser mes besoins', emoji: '🔍' }
      ]
    };
  };

  /**
   * Réponse fallback améliorée pour les actions rapides
   */
  const getFallbackActionResponse = async (actionId: string, label: string): Promise<ChatResponse> => {
    const responses: Record<string, ChatResponse> = {
      'rgpd': {
        content: "Excellente décision ! La conformité RGPD est urgente. 🔒\n\n**Ce que comprend notre audit gratuit :**\n• Analyse de vos traitements de données\n• Identification des risques et non-conformités\n• Plan de mise en conformité personnalisé\n• Estimation des investissements nécessaires\n\n**Bénéfices immédiats :**\n• Éviter les sanctions (jusqu'à 4% du CA)\n• Renforcer la confiance client\n• Améliorer vos processus\n• Sécuriser vos données\n\n**Prêt pour votre audit ?**",
        quickActions: [
          { id: 'rdv_audit', label: 'Planifier l\'audit', emoji: '📅' },
          { id: 'info_rgpd', label: 'En savoir plus', emoji: 'ℹ️' },
          { id: 'contact_expert', label: 'Parler à un expert', emoji: '👨‍💼' }
        ]
      },
      'cybersec': {
        content: "Parfait ! La cybersécurité est critique. 🛡️\n\n**Notre évaluation sécurité comprend :**\n• Scan des vulnérabilités techniques\n• Test des protections actuelles\n• Analyse des comportements utilisateurs\n• Audit des accès et permissions\n\n**Résultats concrets :**\n• Rapport détaillé des risques\n• Plan de sécurisation prioritaire\n• Recommandations budgétées\n• Formation équipe incluse\n\n**Votre entreprise est-elle protégée ?**",
        quickActions: [
          { id: 'scan_securite', label: 'Scanner maintenant', emoji: '🔍' },
          { id: 'formation_cyber', label: 'Formation équipe', emoji: '👥' },
          { id: 'contact_expert', label: 'Parler à un expert', emoji: '👨‍💼' }
        ]
      },
      'facturation': {
        content: "Anticipez l'obligation ! Facturation électronique obligatoire. 📊\n\n**Échéances légales :**\n• 2024 : Grandes entreprises\n• 2025 : PME (+250 salariés)\n• 2026 : Toutes les entreprises\n\n**Bénéfices immédiats :**\n• 60-80% de réduction des coûts\n• Traitement automatisé\n• Conformité garantie\n• Gains de temps considérables\n\n**Notre démonstration live vous montre :**\n• Interface intuitive\n• Intégration avec votre comptabilité\n• ROI calculé pour votre entreprise",
        quickActions: [
          { id: 'demo_live', label: 'Démo live 15min', emoji: '🎥' },
          { id: 'calcul_roi', label: 'Calculer mon ROI', emoji: '💰' },
          { id: 'contact_expert', label: 'Parler à un expert', emoji: '👨‍💼' }
        ]
      },
      'presence': {
        content: "Excellente priorité ! Votre présence en ligne détermine votre succès. 🌐\n\n**État des lieux typique :**\n• 89% des clients cherchent sur Google\n• Vos concurrents captent VOS prospects\n• Chaque jour de retard = clients perdus\n\n**Notre audit SEO gratuit révèle :**\n• Votre position vs concurrents\n• Opportunités manquées\n• Mots-clés à exploiter\n• Plan d'amélioration chiffré\n\n**Résultats clients World Of Numeric :**\n• +150% de trafic organique\n• +300% de leads qualifiés\n• ROI positif dès 3 mois",
        quickActions: [
          { id: 'audit_seo_gratuit', label: 'Audit SEO gratuit', emoji: '🔍' },
          { id: 'strategie_seo', label: 'Stratégie SEO', emoji: '📈' },
          { id: 'contact_expert', label: 'Parler à un expert', emoji: '👨‍💼' }
        ]
      },
      'formation_ia': {
        content: "Anticipez l'avenir ! L'IA transforme tous les métiers. 🤖\n\n**Pourquoi former maintenant :**\n• +40% de productivité mesurée\n• Vos concurrents s'équipent déjà\n• Technologies accessibles à tous\n• ROI immédiat sur les tâches répétitives\n\n**Notre programme formation IA :**\n• Sensibilisation tous niveaux\n• Cas d'usage métiers concrets\n• Outils pratiques immédiatement\n• Accompagnement post-formation\n\n**Bénéfices clients :**\n• Gains temps : 20h/semaine/personne\n• Qualité améliorée\n• Équipes motivées et compétentes",
        quickActions: [
          { id: 'programme_formation', label: 'Voir le programme', emoji: '📚' },
          { id: 'demo_outils_ia', label: 'Démo outils IA', emoji: '🛠️' },
          { id: 'contact_expert', label: 'Parler à un expert', emoji: '👨‍💼' }
        ]
      },
      'contact_expert': {
        content: "Parfait ! Nos experts World Of Numeric sont disponibles. 👨‍💼\n\n**Votre expert dédié vous aide à :**\n• Analyser votre situation spécifique\n• Identifier les priorités urgentes\n• Chiffrer les investissements et ROI\n• Planifier votre transformation\n\n**Modalités de contact :**\n• Formulaire de contact dédié\n• Visioconférence planifiée\n• Rendez-vous sur site possible\n• Premier diagnostic toujours gratuit",
        quickActions: [
          { id: 'appel_urgent', label: 'Appel immédiat', emoji: '📞' },
          { id: 'rdv_planifie', label: 'Planifier RDV', emoji: '📅' },
          { id: 'rdv_site', label: 'RDV sur site', emoji: '🏢' }
        ]
      },
      'default': {
        content: `Excellente démarche concernant "${label}" ! 🎯\n\nC'est un sujet stratégique pour votre entreprise. Chaque situation est unique et mérite une analyse personnalisée.\n\n**World Of Numeric vous accompagne avec :**\n• Diagnostic gratuit de votre situation\n• Solutions sur-mesure adaptées\n• ROI calculé et garanti\n• Accompagnement de A à Z\n\n**Nos clients obtiennent :**\n• Gains de temps : 30-50%\n• Réduction des coûts : 20-40%\n• Croissance chiffre affaires : +25%\n• Équipes plus efficaces et motivées\n\nDiscutons de votre projet spécifique ?`,
        quickActions: [
          { id: 'diagnostic_gratuit', label: 'Diagnostic gratuit', emoji: '📋' },
          { id: 'contact_expert', label: 'Parler à un expert', emoji: '👨‍💼' },
          { id: 'voir_references', label: 'Voir nos références', emoji: '⭐' }
        ]
      }
    };

    return responses[actionId] || responses['default'];
  };

  return (
    <>
      {/* Bouton flottant */}
      {!isOpen && (
        <div 
          className={`fixed ${position === 'bottom-right' ? 'bottom-6 right-6' : 'bottom-6 left-6'} w-16 h-16 rounded-full shadow-lg flex items-center justify-center text-white cursor-pointer transition-all duration-300 hover:scale-105 z-[100]`}
          style={{ 
            background: 'linear-gradient(135deg, #3498db, #2980b9)'
          }}
          onClick={() => setIsOpen(true)}
          title="Assistant WON - World Of Numeric"
        >
          🤖
        </div>
      )}

      {/* Widget de chat */}
      {isOpen && (
        <>
          {/* Overlay (mobile) */}
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm sm:bg-transparent sm:backdrop-blur-0 z-[90]"
            aria-hidden="true"
            onClick={() => isSmallScreen && setIsOpen(false)}
          />

          <div 
            role="dialog" aria-modal="true" aria-label="Assistant WON - World Of Numeric"
            className={`fixed ${
              isSmallScreen
                ? 'inset-x-0 bottom-0 w-full h-[80vh] rounded-t-3xl'
                : position === 'bottom-right'
                  ? 'bottom-6 right-6 w-96 h-[600px] rounded-2xl'
                  : 'bottom-6 left-6 w-96 h-[600px] rounded-2xl'
            } bg-white shadow-2xl flex flex-col border border-gray-200 z-[100]`}
          >
          {/* Header */}
          <div 
            className="rounded-t-2xl p-4 text-white"
            style={{ background: 'linear-gradient(135deg, #3498db, #2980b9)' }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  🤖
                </div>
                <div>
                  <h3 className="font-semibold">WON Assistant IA</h3>
                  <p className="text-xs opacity-90">
                    {aiService ? 'IA Activée' : 'Mode Standard'} • World Of Numeric
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                title="Fermer"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-50 to-white">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[85%] ${
                  msg.role === 'user' 
                    ? 'text-white rounded-2xl p-3 shadow-sm'
                    : 'bg-white text-gray-800 border border-gray-200 rounded-2xl p-3 shadow-sm'
                } `}
                style={{
                  background: msg.role === 'user' ? 'linear-gradient(135deg, #3498db, #2980b9)' : undefined
                }}>
                  <div className="text-sm" dangerouslySetInnerHTML={{ __html: sanitizeHTML(formatMessage(msg.content)) }} />
                  <div className="text-[10px] text-gray-400 mt-1">{formatTimestamp(msg.timestamp)}</div>
                  
                  {/* Actions rapides améliorées */}
                  {msg.quickActions && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {msg.quickActions.map((action) => (
                        <button
                          key={action.id}
                          onClick={() => handleQuickAction(action.id, action.label)}
                          disabled={isLoading}
                          className={`
                            inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium
                            transform-gpu transition-transform duration-150 hover:scale-105 hover:-translate-y-0.5 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ring-1
                            ${msg.role === 'user'
                              ? 'bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm ring-white/20'
                              : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-md hover:shadow-lg ring-white/0'
                            }
                          `}
                        >
                          <span className="text-base">{action.emoji}</span>
                          <span>{action.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {/* Indicateur de typing */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 border border-gray-200 rounded-2xl p-3 shadow-sm">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-2xl">
            {/* Suggestions contextuelles */}
            {!isLoading && !message.trim() && (
              <div className="mb-2 flex flex-wrap gap-2">
                {composerSuggestions.map((sugg) => (
                  <button
                    key={sugg}
                    onClick={() => setMessage(sugg)}
                    className="px-3 py-1.5 rounded-full text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                    title={sugg}
                  >
                    {sugg}
                  </button>
                ))}
              </div>
            )}
            <div className="flex space-x-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                disabled={isLoading}
                placeholder={isLoading ? "En cours..." : "Posez votre question..."}
                className="flex-1 p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <button 
                onClick={handleSendMessage}
                disabled={!message.trim() || isLoading}
                className="px-4 py-3 text-white rounded-xl hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-all"
                style={{ background: 'linear-gradient(135deg, #3498db, #2980b9)' }}
              >
                {isLoading ? '⏳' : '➤'}
              </button>
            </div>
            <div className="text-xs text-gray-500 mt-2 text-center">
              {aiService ? 'IA' : 'Standard'} • Propulsé par World Of Numeric
            </div>
          </div>
          </div>
        </>
      )}
    </>
  );
};

export default SmartChatbot;
